import React, { useEffect, useState } from "react";
import moment from "moment";
import ApiConfig from "../../common/config/ApiConfig";
import { toast } from "react-toastify";
import { useLanguage } from "../../common/i18n/LanguageContext";

function TopupCreditModal({ isOpen, onClose, onSuccess, agentId }) {
  const { t } = useLanguage();
  const [credit, setCredit] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCredit("");
      fetchHistory();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const fetchHistory = () => {
    ApiConfig.post("/gettopupcredit").then((result) => {
      if (result.resCode === 200) {
        setHistory(result.data);
      }
    });
  };

  const handleClose = () => {
    setCredit("");
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const amount = parseFloat(credit);
    if (!credit || isNaN(amount) || amount <= 0) {
      toast.error(t("membertable_credit_invalid"), { position: "top-right", autoClose: 3000 });
      return;
    }
    if (!agentId) {
      toast.error(t("topup_credit_modal_user_not_found"), { position: "top-right", autoClose: 3000 });
      return;
    }
    setLoading(true);
    ApiConfig.post("/addtopupcredit", { createBy: agentId, credit: amount })
      .then((result) => {
        if (result.resCode !== 200) {
          toast.error(result.message || t("topup_credit_modal_error"), {
            position: "top-right",
            autoClose: 3000,
          });
          return;
        }
        toast.success(t("topup_credit_modal_success"), { autoClose: 2000 });
        setCredit("");
        fetchHistory();
        if (onSuccess) onSuccess();
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-[560px] max-w-[95%] p-5 flex flex-col gap-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-brand-blue-700">
            {t("topup_credit_modal_title")}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div className="flex flex-col">
            <label className="text-sm font-semibold mb-1">
              {t("membertable_credit_amount_label")}
            </label>
            <input
              type="number"
              min="1"
              step="any"
              className="border border-gray-300 rounded px-2 py-1 focus:outline-none focus:border-brand-blue-500"
              value={credit}
              onChange={(e) => setCredit(e.target.value)}
              placeholder={t("membertable_credit_amount_placeholder")}
            />
          </div>
          <div className="flex flex-row gap-2">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 h-[2.2rem] bg-gradient-to-b from-brand-gold-300 via-brand-gold-400 to-brand-gold-600 text-brand-ink-onGold border border-brand-gold-200 shadow-gold hover:brightness-105 disabled:opacity-50 text-[14px] font-bold rounded"
            >
              {loading ? t("common_saving") : t("topup_credit_modal_title")}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 h-[2.2rem] bg-slate-400 hover:bg-slate-500 text-white text-[14px] font-bold rounded"
            >
              {t("common_cancel")}
            </button>
          </div>
        </form>

        <div>
          <h3 className="text-sm font-bold text-gray-600 mb-2">
            {t("topup_credit_modal_history_title")}
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-brand-blue-700 text-white">
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">
                    {t("membertable_credit_amount_label")}
                  </th>
                  <th className="px-3 py-2 text-left">
                    {t("topup_credit_modal_col_by")}
                  </th>
                  <th className="px-3 py-2 text-left">{t("common_date_label")}</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-4 text-gray-400">
                      {t("common_no_data")}
                    </td>
                  </tr>
                ) : (
                  history.map((row, index) => (
                    <tr
                      key={row.id}
                      className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    >
                      <td className="px-3 py-2 text-gray-600">{row.id}</td>
                      <td className="px-3 py-2 font-semibold text-green-600">
                        {parseFloat(row.credit).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {row.createBy?.Username ?? "-"}
                      </td>
                      <td className="px-3 py-2 text-gray-500">
                        {moment(row.createAt).format("DD/MM/YYYY HH:mm")}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TopupCreditModal;
